import { isPlatformServer } from '@angular/common';
import {
  inject,
  Injectable,
  makeStateKey,
  PLATFORM_ID,
  TransferState,
} from '@angular/core';
import { environment } from '@/environments/environment';

const STORAGE_STATE_KEY = makeStateKey<[string, string][]>('APP_STORAGE_STATE');

/**
 * Prefixo por ambiente (prod/homolog/local): evita que deploys diferentes
 * servidos no mesmo domínio compartilhem token/estado no localStorage.
 */
const PREFIXO = environment.storagePrefix;

@Injectable({ providedIn: 'root' })
export class LocalStorage {
  // Dependencies
  private transferState = inject(TransferState);

  // State
  private isServer = isPlatformServer(inject(PLATFORM_ID));
  private storage = new Map<string, string>();

  constructor() {
    // Initialize the localStorage with the transfer state
    if (!this.isServer) {
      new Map(this.transferState.get(STORAGE_STATE_KEY, [])).forEach(
        (value, key) => {
          localStorage.setItem(key, value);
        }
      );
    }
  }

  /**
   * Updates the transfer state with the given map.
   */
  private updateTransferState(map: Map<string, string>) {
    this.transferState.set(STORAGE_STATE_KEY, Array.from(map.entries()));
  }

  /**
   * Returns the number of key/value pairs.
   */
  get length(): number {
    if (this.isServer) {
      return this.storage.size;
    }
    return this.chavesDoAmbiente().length;
  }

  /** Chaves brutas do localStorage que pertencem a este ambiente. */
  private chavesDoAmbiente(): string[] {
    const chaves: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(PREFIXO)) chaves.push(k);
    }
    return chaves;
  }

  /**
   * Sets the value of the pair identified by key to value,
   * creating a new key/value pair if none existed for key previously.
   */
  setItem(key: string, value: string): void {
    const chave = PREFIXO + key;
    if (this.isServer) {
      this.storage.set(chave, value);
      this.updateTransferState(this.storage);
      return;
    }

    localStorage.setItem(chave, value);
  }

  /**
   * Returns the current value associated with the given key,
   * or null if the given key does not exist in the list associated
   * with the object.
   */
  getItem(key: string): string | null {
    const chave = PREFIXO + key;
    if (this.isServer) {
      return this.storage.get(chave) ?? null;
    }

    return localStorage.getItem(chave);
  }

  /**
   * Removes the key/value pair with the given key,
   * if a key/value pair with the given key exists.
   */
  removeItem(key: string): void {
    const chave = PREFIXO + key;
    if (this.isServer) {
      this.storage.delete(chave);
      this.updateTransferState(this.storage);
      return;
    }

    localStorage.removeItem(chave);
  }

  /**
   * Removes all key/value pairs, if there are any.
   */
  clear(): void {
    if (this.isServer) {
      this.storage.clear();
      this.updateTransferState(this.storage);
      return;
    }

    // Remove apenas as chaves deste ambiente, preservando as dos demais
    for (const chave of this.chavesDoAmbiente()) {
      localStorage.removeItem(chave);
    }
  }

  /**
   * Returns the name of the nth key in the list.
   */
  key(index: number): string | null {
    if (this.isServer) {
      const chave = Array.from(this.storage.keys())[index] ?? null;
      return chave ? chave.slice(PREFIXO.length) : null;
    }

    const chave = this.chavesDoAmbiente()[index] ?? null;
    return chave ? chave.slice(PREFIXO.length) : null;
  }
}
